import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, MonitorPlay, BookOpen, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const NotFoundPage = () => {
  const redirectOptions = [
    {
      icon: Home,
      title: 'Homepage',
      description: 'Return to our main landing page',
      path: '/',
      external: false,
      color: 'from-[#123B7A] to-[#0EA5FF]'
    },
    {
      icon: MonitorPlay,
      title: 'Digital Learning Portal',
      description: 'Access our comprehensive learning resources',
      path: 'https://dlp.physicsnotebook.in',
      external: true,
      color: 'from-[#0EA5FF] to-cyan-500'
    },
    {
      icon: BookOpen,
      title: 'Blog',
      description: 'Read our latest physics articles and tips',
      path: 'https://blog.physicsnotebook.in',
      external: true,
      color: 'from-indigo-600 to-[#123B7A]'
    }
  ];

  return (
    <>
      <Helmet>
        <title>404 - Page Not Found | PhysicsNotebook.in</title>
        <meta name="description" content="The page you're looking for doesn't exist. Redirect to our homepage, digital learning portal, or blog." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* 404 Number */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <h1 className="text-9xl md:text-[200px] font-bold bg-gradient-to-r from-[#123B7A] to-[#0EA5FF] bg-clip-text text-transparent">
              404
            </h1>
          </motion.div>

          {/* Error Message */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-12"
          >
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-gray-900 mb-4">
              This page didn't make the cut.
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              No guessing here either — the page you're looking for doesn't exist. Here's where you probably meant to go.
            </p>
          </motion.div>

          {/* Redirect Options */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
          >
            {redirectOptions.map((option) => {
              const CardInner = (
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-100 h-full"
                >
                  <div className={`bg-gradient-to-r ${option.color} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <option.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{option.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{option.description}</p>
                  <div className="flex items-center justify-center text-[#123B7A] group-hover:text-[#0EA5FF] font-medium">
                    Go there
                    <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </div>
                </motion.div>
              );

              return option.external ? (
                <a key={option.path} href={option.path} target="_blank" rel="noopener noreferrer" className="group">
                  {CardInner}
                </a>
              ) : (
                <Link key={option.path} to={option.path} className="group">
                  {CardInner}
                </Link>
              );
            })}
          </motion.div>

          {/* Additional Help */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-white rounded-xl p-6 shadow-md max-w-2xl mx-auto"
          >
            <p className="text-gray-600 mb-4">
              If you believe this is an error, please contact our support team.
            </p>
            <Link to="/contact">
              <Button variant="outline" className="border-[#123B7A] text-[#123B7A] hover:bg-blue-50">
                Contact Support
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default NotFoundPage;
